import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useMemo, useState } from 'react';
import EditPositionModal from 'components/IncidentConfig/EditPositionModal';
import IncidentNewSeatDialog from 'components/IncidentConfig/IncidentNewSeatDialog';
import AddResourceToRosterModal from 'components/IncidentConfig/roster/AddResourceToRosterModal';
import Tree from 'components/DesignSystems/OrganizationChart/Tree';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  RosterResource,
  useRosterResources,
} from 'components/IAP/hooks/incidentRosterResources/incidentRosterResources';
import { buildSeatBox } from './IncidentRosterSeatBoxes';
import {
  Position,
  QuickAddHandler,
  QuickAddTarget,
  ResourceHandlers,
} from './incidentRosterChart.types';
import {
  buildTree,
  checkChildrenForMembers,
  renderResourceNodes,
} from './incidentRosterChart.utils';
import { Sections } from './incidentRosterChart.constants';

const IncidentRosterBlockChart: React.FC<{
  incidentMembers: RosterEntry[];
}> = ({ incidentMembers }) => {
  const incidentPositionsQuery = useGetPositionsForIncident();
  const incidentPositions = incidentPositionsQuery.data || [];

  const positions: Position[] = incidentPositions
    .filter((pos) => !(pos.is_template && pos.name === 'Qualified Individual'))
    .map((pos) => ({
      positionId: pos.id,
      incidentSeat: pos.name,
      parentSeat: pos.parent_name,
      isWorkgroup: pos.is_workgroup,
    }));

  const [showAssignedPositions, setShowAssignedPositions] = useState(false);
  const [showEditPositionModalForPosition, setShowEditPositionModal] =
    useState('');
  const [editingResource, setEditingResource] = useState<RosterResource | null>(
    null
  );
  const [quickAddPositionParent, setQuickAddPositionParent] =
    useState<QuickAddTarget | null>(null);
  const [quickAddResourceParent, setQuickAddResourceParent] =
    useState<QuickAddTarget | null>(null);

  const rosterResourcesQuery = useRosterResources();

  const resourcesByPosition = useMemo(() => {
    const map = new Map<string, RosterResource[]>();
    (rosterResourcesQuery.data ?? []).forEach((resource) => {
      const existing = map.get(resource.reportsToPosition) ?? [];
      existing.push(resource);
      map.set(resource.reportsToPosition, existing);
    });
    return map;
  }, [rosterResourcesQuery.data]);

  const resourceHandlers: ResourceHandlers = {
    resourcesByPosition,
    onEditResource: (resource: RosterResource) => setEditingResource(resource),
  };

  const onQuickAdd: QuickAddHandler = (type, positionId, seatName) => {
    const target: QuickAddTarget = { positionId, seatName };
    if (type === 'position') {
      setQuickAddPositionParent(target);
    } else {
      setQuickAddResourceParent(target);
    }
  };

  return (
    <div
      className="form-block chart"
      style={{ overflowX: 'auto', width: '100%' }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          onChange={(e) => setShowAssignedPositions(e.target.checked)}
          checked={showAssignedPositions}
        />
        Only show positions with assigned members
      </label>
      {positions
        .filter((pos) => pos.parentSeat === null)
        .sort((a, b) => a.incidentSeat.localeCompare(b.incidentSeat))
        .map((rootPosition) => {
          const membersForSeat = incidentMembers.filter((member) =>
            member.incidentPositions?.some(
              (pos) => pos.positionId === rootPosition.positionId
            )
          );

          if (membersForSeat.length === 0) {
            const hasChildWithMembers = checkChildrenForMembers({
              parentSeat: rootPosition.incidentSeat,
              incidentMembers,
              positions,
            });

            if (showAssignedPositions && !hasChildWithMembers) {
              return null; // Skip if no members for this root position and its children
            }
          }

          const currentSection =
            Sections.find(
              (section) => section.sectionSeatName === rootPosition.incidentSeat
            ) || null;

          return (
            <>
              <br />
              <Tree
                key={rootPosition.incidentSeat}
                label={buildSeatBox({
                  positionId: rootPosition.positionId,
                  seatName: rootPosition.incidentSeat,
                  members: membersForSeat,
                  isWorkteam: rootPosition.isWorkgroup,
                  setShowEditPositionModal: setShowEditPositionModal,
                  onQuickAdd,
                })}
              >
                {buildTree(
                  rootPosition.incidentSeat,
                  incidentMembers,
                  positions,
                  showAssignedPositions,
                  setShowEditPositionModal,
                  resourceHandlers,
                  onQuickAdd,
                  currentSection
                )}
                {renderResourceNodes(rootPosition.positionId, resourceHandlers)}
              </Tree>
            </>
          );
        })}
      {showEditPositionModalForPosition &&
        showEditPositionModalForPosition !== '' && (
          <EditPositionModal
            show={showEditPositionModalForPosition !== ''}
            onHide={() => setShowEditPositionModal('')}
            positionId={showEditPositionModalForPosition}
            incidentMembers={incidentMembers}
          />
        )}
      {editingResource && (
        <AddResourceToRosterModal
          show={true}
          onClose={() => setEditingResource(null)}
          editingResource={editingResource}
        />
      )}
      {quickAddPositionParent && (
        <IncidentNewSeatDialog
          show={true}
          onClose={() => setQuickAddPositionParent(null)}
          presetReportsTo={{
            id: quickAddPositionParent.positionId,
            name: quickAddPositionParent.seatName ?? '',
          }}
        />
      )}
      {quickAddResourceParent && (
        <AddResourceToRosterModal
          show={true}
          onClose={() => setQuickAddResourceParent(null)}
          presetPositionId={quickAddResourceParent.positionId}
        />
      )}
    </div>
  );
};

export default IncidentRosterBlockChart;
